import styled from 'styled-components';
import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Button, Avatar } from '@leapeasy/ui-kit';
import { Checkbox } from '@mui/material';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  INVITE_LANDLORD_TABLE_DEFAULT_VISIBLE_KEYS,
  INVITE_LANDLORD_TABLE_STORAGE_KEY,
} from './inviteLandlordTableConfig';

const dateColumnOptions = (overrides = {}) => ({
  width: 104,
  minWidth: 104,
  maxWidth: 104,
  shrink: false,
  ...overrides,
});

const checkboxSx = {
  color: '#69236b',
  padding: 0,
  margin: 0,
  '&.Mui-checked .MuiSvgIcon-root': {
    borderColor: '#69236b',
    color: '#69236b',
  },
};

const formatLandlordAddress = (landlord) =>
  [
    landlord.billingStreet || '',
    landlord.billingCity || '',
    landlord.billingState || '',
    landlord.billingPostalCode || '',
  ]
    .join(' ')
    .trim();

export const InviteLandlordTable = forwardRef(
  (
    {
      landlords,
      setOpen,
      filter,
      updateLandlord,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      inviteSending = false,
    },
    ref,
  ) => {
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));

    const [valueOfAllCheck, setValueOfAllCheck] = useState(false);

    useEffect(() => {
      setValueOfAllCheck(
        landlords.length > 0 &&
          landlords.filter((item) => item.check === true).length === landlords.length,
      );
    }, [landlords]);

    const buildInviteLandlordRow = useCallback(
      (landlord, index) => {
        const address = formatLandlordAddress(landlord);
        const image = landlord.user ? landlord.user.image : null;

        return [
          index,
          isDemo ? getDemoData('landlord-name') : landlord.name,
          isDemo ? getDemoData('email') : landlord.email_address,
          new Date(landlord.sf_createdDate).toISOString().slice(0, 10),
          landlord.total_buildings ? landlord.total_buildings : 0,
          landlord.total_units ? landlord.total_units : 0,
          image,
          isDemo ? getDemoData('address') : address,
          isDemo ? getDemoData('phone') : landlord.phone,
          landlord.id,
        ];
      },
      [isDemo],
    );

    const tableData = useMemo(() => {
      if (!landlords) return [];
      return landlords.map((landlord, index) => buildInviteLandlordRow(landlord, index));
    }, [buildInviteLandlordRow, landlords]);

    const handleToggleAll = useCallback(
      (checked) => {
        if (inviteSending) return;
        const tmp = landlords.map((item) => ({ ...item, check: checked }));
        updateLandlord([...tmp]);
        setValueOfAllCheck(checked);
      },
      [inviteSending, landlords, updateLandlord],
    );

    const handleToggleRow = useCallback(
      (targetLandlord, checked) => {
        if (inviteSending || !targetLandlord) return;
        const tmp = landlords.map((item) =>
          item.id === targetLandlord.id ? { ...item, check: checked } : item,
        );
        updateLandlord(tmp);
      },
      [inviteSending, landlords, updateLandlord],
    );

    const handleSendInvite = useCallback(
      (rowIndex) => {
        if (inviteSending) return;
        const targetLandlord = Number.isInteger(rowIndex) ? landlords[rowIndex] : null;
        if (!targetLandlord) return;
        const tmp = landlords.map((item) =>
          item.id === targetLandlord.id ? { ...item, check: true } : item,
        );
        updateLandlord(tmp);
        setOpen(true);
      },
      [inviteSending, landlords, setOpen, updateLandlord],
    );

    const columns = useMemo(
      () => [
        {
          name: (
            <CheckboxCell>
              <Checkbox
                disableRipple
                sx={checkboxSx}
                description=""
                label=""
                checked={valueOfAllCheck}
                disabled={inviteSending}
                onChange={(e) => handleToggleAll(e.target.checked)}
              />
            </CheckboxCell>
          ),
          key: 'select',
          options: {
            width: 48,
            minWidth: 48,
            shrink: false,
            align: 'center',
            excludeFromExport: true,
            customBodyRenderer: (value, tableMeta) => {
              const rowIndex = Number(value);
              const landlordId = tableMeta?.[tableMeta.length - 1];
              const byIndex = Number.isInteger(rowIndex) ? landlords[rowIndex] : null;
              const targetLandlord = byIndex || landlords.find((item) => item.id === landlordId);
              const checked = Boolean(targetLandlord?.check);

              return (
                <CheckboxCell>
                  <Checkbox
                    sx={checkboxSx}
                    disableRipple
                    description=""
                    label=""
                    checked={checked}
                    disabled={inviteSending}
                    onChange={(e) => handleToggleRow(targetLandlord, e.target.checked)}
                  />
                </CheckboxCell>
              );
            },
          },
        },
        {
          name: 'Account Name',
          key: 'name',
          options: {
            minWidth: 140,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
          },
        },
        {
          name: 'Email',
          key: 'email_address',
          options: {
            ...fitContentTextColumnOptions({ fitContentCharWidth: 10, maxWidth: 320, flexGrow: 0 }),
            sort: true,
          },
        },
        {
          name: 'Create date',
          key: 'create_date',
          options: {
            ...dateColumnOptions(),
            sort: true,
          },
        },
        {
          name: 'Property Count',
          key: 'total_buildings',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 88, maxWidth: 120, align: 'center' }),
            sort: true,
            filter: true,
            filterOptions: [
              { text: '1', value: '1' },
              { text: '2', value: '2' },
              { text: '3', value: '3' },
            ],
          },
        },
        {
          name: 'Unit Count',
          key: 'total_units',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 72, maxWidth: 96, align: 'center' }),
            sort: true,
            filter: true,
            filterOptions: [
              { text: '1', value: '1' },
              { text: '2', value: '2' },
              { text: '3', value: '3' },
            ],
          },
        },
        {
          name: 'Profile',
          key: 'avatar',
          options: {
            width: 56,
            shrink: false,
            excludeFromExport: true,
            customBodyRenderer: (value) => (
              <Avatar
                size="medium"
                iconImage={value ? <img src={value} alt="" /> : <IconGraphy icon="Users.User" />}
              />
            ),
          },
        },
        {
          name: 'Address',
          key: 'billingStreet',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 360 }),
            sort: true,
          },
        },
        {
          name: 'Phone Number',
          key: 'phone_number',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 180 }),
            sort: true,
          },
        },
        {
          name: 'Send Invite',
          key: 'action',
          options: {
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (_value, tableMeta) => (
              <SendInviteCell>
                <Tooltip title="Send invite" placement="top" showCarot>
                  <span>
                    <Button
                      variant="secondary"
                      iconSuffix="EditorLayout.Send"
                      disabled={inviteSending}
                      onClick={() => handleSendInvite(Number(tableMeta?.[0]))}
                    >
                      Send
                    </Button>
                  </span>
                </Tooltip>
              </SendInviteCell>
            ),
          },
        },
      ],
      [
        handleSendInvite,
        handleToggleAll,
        handleToggleRow,
        inviteSending,
        landlords,
        valueOfAllCheck,
      ],
    );

    return (
      <>
        <Table
          showEmptyState
          emptyContent={
            <GlobalUXState
              variant={filter?.searchText ? 'noResults' : 'noData'}
              compact
              description={
                filter?.searchText
                  ? 'No operators match your search.'
                  : 'No operators available to invite yet.'
              }
            />
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={INVITE_LANDLORD_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={INVITE_LANDLORD_TABLE_STORAGE_KEY}
          columnWidthStorageKey={INVITE_LANDLORD_TABLE_STORAGE_KEY}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((landlord, index) =>
                    buildInviteLandlordRow(landlord, index),
                  );
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((landlord, index) =>
                    buildInviteLandlordRow(landlord, index),
                  );
                }
              : undefined
          }
          data={tableData}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Invite Operator List"
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Invite Operator List"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

InviteLandlordTable.displayName = 'InviteLandlordTable';

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const SendInviteCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
